import { Helmet } from 'react-helmet-async';
import { resolveSeo, type SEOVariant } from '../lib/siteMeta';

export type { SEOVariant };

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  variant?: SEOVariant;
  /** Pass the page title of a project to get BreadcrumbList + Article OG type */
  projectTitle?: string;
}

/**
 * Renders the page head. All the route metadata and JSON-LD lives in
 * ../lib/siteMeta so the runtime head and the build-time prerender
 * (scripts/prerender.mjs) are guaranteed to agree — change copy there, not here.
 */
export function SEO({ title, description, image, url, variant = "project", projectTitle }: SEOProps) {
  const seo = resolveSeo({ title, description, image, url, variant, projectTitle });

  return (
    <Helmet>
      <title>{seo.pageTitle}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.canonical} />

      {/* Privacy & Anti-Scraping of Personal Data */}
      <meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />

      {/* Open Graph */}
      <meta property="og:type" content={seo.ogType} />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={seo.pageTitle} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.absoluteImage} />
      <meta property="og:image:alt" content={seo.pageTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.canonical} />
      <meta name="twitter:title" content={seo.pageTitle} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.absoluteImage} />

      {/* Structured Data */}
      {seo.schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}
